import { TrainListRange } from "features/CenterContent/RoleContent/ProductionDeviation/useTrainListRange";
import { Dispatch, FC, Fragment, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { FilterChip, Flex, Separator, Text } from "shared/components/Spor";
import { unique } from "shared/utils/arrayUtils";
import { toggle } from "shared/utils/objectUtils";
import { useTheme } from "styled-components";

type TrainSerieFilterProps = {
  filters: string[];
  setFilters: Dispatch<SetStateAction<string[]>>;
} & TrainListRange;

export const TrainSerieFilter: FC<TrainSerieFilterProps> = ({
  filters,
  setFilters,
  trainList,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const lineIds = unique(
    trainList.flatMap((it) => it.lineIds.map(({ lineId }) => lineId)),
  );

  const toRoute = (train: (typeof trainList)[number]) =>
    `${train.originStopName}–${train.destinationStopName}`;

  const routesByLineId = trainList.reduce<Record<string, string[]>>(
    (acc, train) => {
      const route = toRoute(train);

      train.lineIds.forEach(({ lineId }) => {
        if (!acc[lineId]) {
          acc[lineId] = [];
        }

        if (!acc[lineId].includes(route)) {
          acc[lineId].push(route);
        }
      });

      return acc;
    },
    {},
  );

  lineIds.forEach((lineId) => {
    routesByLineId[lineId]?.sort();
  });

  const toggleMany = (prev: string[], items: string[]) => {
    const allSelected = items.every((x) => prev.includes(x));
    if (allSelected) {
      return prev.filter((x) => !items.includes(x));
    }
    return unique([...prev, ...items]);
  };

  const chip = (label: string, checked: boolean, onClick: () => void) => (
    <FilterChip
      key={label}
      size="xs"
      checked={checked}
      onCheckedChange={onClick}
      borderRadius="6px"
    >
      <Text variant="xs">{label}</Text>
    </FilterChip>
  );

  return (
    <Flex flexDir="column" gap="12px">
      <Flex gap="12px" flexWrap="wrap" alignItems="center">
        <Text variant="xs">{t("Filtrer på:")}</Text>

        {lineIds.map((lineId, i) => {
          const routes = routesByLineId[lineId] ?? [];
          const allItems = [lineId, ...routes];
          const allSelected = allItems.every((x) => filters.includes(x));

          return (
            <Fragment key={lineId}>
              <Flex gap="8px" flexWrap="wrap" alignItems="center">
                {chip(lineId, allSelected, () =>
                  setFilters((prev) => toggleMany(prev, allItems)),
                )}

                {routes.map((route) =>
                  chip(route, filters.includes(route), () =>
                    setFilters((prev) => toggle(prev, route)),
                  ),
                )}
              </Flex>
              {i < lineIds.length - 1 && (
                <Separator borderTop={`1px solid ${theme.colorOutlineMain}`} />
              )}
            </Fragment>
          );
        })}
      </Flex>
    </Flex>
  );
};
